import {createActions} from 'redux-actions';
import fetchJson, {deleteJson, postJson, putJson} from '../fetchJson';
import {push} from 'connected-react-router';
import handleError from '../handleError';
import io from 'socket.io-client';
import chatActions from './chat.actions';
import notificationsActions from './notifications.actions';
import {uiActions} from '../chrome';
import {profileActions} from '../profile';
import ReactGA from 'react-ga';

const actions = createActions(
	'SESSION_LOGIN_WAITING',
	'SESSION_LOGIN_SUCCESS',
	'SESSION_LOGIN_ERROR',
	'SESSION_LOGIN_EXPIRED',
	'SESSION_LOGOUT_SUCCESS',
	'SESSION_UPDATE_UNIQUE_NAME',
	'SESSION_UPDATE_AVATAR_IMAGE',
	'SESSION_UPDATE_SUBSCRIPTION_LEVEL',
	'SESSION_FAVOURITES_RETRIEVING',
	'SESSION_FAVOURITES_RETRIEVED',
	'SESSION_FAVOURITE_SET',
	'SESSION_FAVOURITE_UNSET',
	'SESSION_USER_WELCOMED',
	'SESSION_SET_SOCKET_CLEANUP',
	'SESSION_UPDATE_CURRENT_PROVIDER',
	'SESSION_USERNAME_CHANGED',
	'SESSION_SIGNUP_COMMUNITY',
	'SESSION_PROFILE_FIRST_TIME',
	'SESSION_UPDATE_MEMBERSHIP_LIMIT',
	'SESSION_AFFILIATIONS_LOADING',
	'SESSION_AFFILIATIONS_LOADED',
	'SESSION_AFFILIATIONS_FAILED'
);

export const checkLoginStatus = () => (dispatch, getState) => {
	fetchJson('/api/login/status')
		.then(result => {
			ReactGA.event({
				category: 'Session',
				action: 'Return'
			});

			dispatch(actions.sessionLoginSuccess(result));
			dispatch(profileActions.retrieveOnlyProfile(result.provider.profile.uniqueName));

			setupSocket(dispatch);
			if (result && result.user && !result.user.welcomed) {
				dispatch(uiActions.uiModal(null));
				dispatch(push('/getting-started'));
			} else if (localStorage.getItem('loginShown') !== 'true') {
				dispatch(
					uiActions.uiMessage(`Successfully logged in. Welcome back ${result.user.firstname}!`)
				);
			}
			localStorage.setItem('loginShown', 'true');
		})
		.then(() => dispatch(getSessionFavourites()))
		.catch(err => {
			// Do nothing. No session yet.
			localStorage.removeItem('loginShown');
		});
};

export const login = (username, password) => (dispatch, getState) => {
	ReactGA.event({
		category: 'Session',
		action: 'Login'
	});

	dispatch(actions.sessionLoginWaiting());
	postJson('/api/login/', {username, password})
		.then(result => {
			// reload window to prompt browsers to save login info
			window.location.reload();
		})
		.catch(err => {
			dispatch(actions.sessionLoginError());
			localStorage.removeItem('loginShown');
			dispatch(uiActions.uiModal('login'));
		});
};

export const signup = (email, password) => (dispatch, getState) => {
	ReactGA.event({
		category: 'On-boarding',
		action: 'Sign-up via email'
	});

	dispatch(actions.sessionLoginWaiting());
	postJson('/api/user/', {email, password})
		.then(result => {
			dispatch(actions.sessionLoginSuccess(result));
			localStorage.setItem('loginShown', 'true');
			dispatch(push('/@' + result.provider.profile.uniqueName));
			setupSocket(dispatch);
			if (result && result.user && !result.user.welcomed) {
				dispatch(uiActions.uiModal(null));
				dispatch(push('/getting-started'));
			}
		})
		.then(() => dispatch(getSessionFavourites()))
		.catch(handleError);
};

let socket;

const setupSocket = dispatch => {
	socket = io();
	socket.on('chat.message.received', payload => dispatch(chatActions.receiveMessage(payload)));
	socket.on('notification', payload => dispatch(notificationsActions.addNotification(payload)));
	dispatch(
		actions.sessionSetSocketCleanup(() => {
			actions.sessionSetSocketCleanup(null);
			if (socket) socket.close();
		})
	);
};

const emit = (msg, payload) => {
	socket.emit(msg, payload);
};

export const closeAccount = (uuid, reason) => (dispatch, getState) => {
	ReactGA.event({
		category: 'On-boarding',
		action: 'Close account'
	});
	postJson(`/api/user/${uuid}/delete`, {reason})
		.then(() => {
			const {session} = getState();
			if (session && session.socketCleanup) session.socketCleanup();
			dispatch(push('/'));
			dispatch(actions.sessionLogoutSuccess());
			localStorage.removeItem('userEmail');
		})
		.catch(handleError(dispatch));
};

export const logout = () => (dispatch, getState) => {
	ReactGA.event({
		category: 'Session',
		action: 'Logout'
	});
	postJson('/api/logout/', {})
		.then(() => {
			const {session} = getState();
			if (session && session.socketCleanup) session.socketCleanup();
			dispatch(actions.sessionLogoutSuccess());
			localStorage.removeItem('loginShown');
			dispatch(push('/'));
		})
		.catch(handleError(dispatch));
};

export const getSessionFavourites = () => (dispatch, getState) => {
	const {session} = getState();

	if (!session || !session.current || !session.current.provider) {
		throw Error('Should not be able to favourite without being logged in');
	}

	dispatch(actions.sessionFavouritesRetrieving());

	fetchJson(`/api/providers/${session.current.provider.uuid}/favourites`)
		.then(result => dispatch(actions.sessionFavouritesRetrieved(result)))
		.catch(handleError);
};

export const setFavourite = (uuid, isFavourite = true) => (dispatch, getState) => {
	const {session, profile} = getState();

	if (!session || !session.current || !session.current.provider) {
		handleError(dispatch)();
		return;
	}

	ReactGA.event({
		category: 'Collections',
		action: 'Collected'
	});

	dispatch(isFavourite ? actions.sessionFavouriteSet(uuid) : actions.sessionFavouriteUnset(uuid));

	const poster = isFavourite ? postJson : deleteJson;

	poster(`/api/providers/${session.current.provider.uuid}/favourites/${uuid}`, {})
		.then(() => {
			// Also need to update current profile state if viewing own profile and your favouriting
			if (isFavourite && (profile && profile.uuid === session.current.provider.uuid)) {
				dispatch(profileActions.refreshFavourites());
			}
		})
		.catch(handleError(dispatch));
};

export const handleSessionError = error => (dispatch, getState) => {
	handleError(dispatch)(error);
};

export const acceptWelcome = subscriptionLevel => (dispatch, getState) => {
	const {session} = getState();

	ReactGA.event({
		category: 'On-boarding',
		action: 'Welcomed'
	});

	dispatch(
		actions.sessionUserWelcomed({
			uuid: session.current.provider.uuid,
			subscriptionLevel
		})
	);

	putJson('/api/providers/' + session.current.provider.uuid, {
		subscriptionLevel,
		user: {
			welcomed: true
		}
	})
		// Re-direct if uniqueName changes
		.then(() => {})
		.catch(handleError(dispatch));
};

export const persistSessionField = providerData => (dispatch, getState) => {
	const {session} = getState();

	if (!session || !session.current || !session.current.provider)
		throw Error('Expected a session when updating welcomed user, but none found.');

	const data = {...providerData};
	providerData.user = providerData.user || {};
	providerData.user.welcomed = true;

	if (providerData.user && providerData.user.location) {
		// Don't save whole location object, just the suburbUuid, KTHX
		data.user = {
			...data.user,
			suburbUuid: providerData.user.location.suburbUuid
		};
		delete data.user.location;
	}

	dispatch(actions.sessionUpdateCurrentProvider(providerData));

	putJson('/api/providers/' + session.current.provider.uuid, data)
		.then(({uniqueName}) => {
			if (uniqueName) {
				// Update session unique name so we don't break things
				dispatch(actions.sessionUpdateUniqueName(uniqueName));
			}
		})
		.catch(handleError(dispatch));
};

export const changePassword = () => (dispatch, getState) => {
	const {session} = getState();

	postJson(`/api/user/${encodeURIComponent(session.current.user.username)}/reset/request`, {})
		.then(() =>
			dispatch(
				uiActions.uiMessage(
					`A password change link has been sent to your email. Please open it to change your password securely.`
				)
			)
		)
		.catch(handleError(dispatch));
};

export const updateExceededLimit = () => dispatch => {
	fetchJson('/api/login/status')
		.then(res => {
			dispatch(actions.sessionUpdateMembershipLimit(res.user.exceededMembershipLimit));
		})
		.catch(handleError(dispatch));
};

export const retrieveSessionAffiliations = uuid => (dispatch, getState) => {
	dispatch(actions.sessionAffiliationsLoading());

	fetchJson('/api/affiliations/?userUuid=' + uuid)
		.then(affiliations => dispatch(actions.sessionAffiliationsLoaded(affiliations)))
		.catch(error => {
			dispatch(actions.sessionAffiliationsFailed());
			handleError(dispatch);
		});
};

export default {
	...actions,
	checkLoginStatus,
	login,
	signup,
	logout,
	setFavourite,
	emit,
	closeAccount,
	handleSessionError,
	acceptWelcome,
	changePassword,
	persistSessionField,
	updateExceededLimit,
	retrieveSessionAffiliations
};



// WEBPACK FOOTER //
// ./src/user/session.actions.js